import { memo, useCallback, useEffect, useMemo, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import ButtonGroup from '@atlaskit/button/button-group';
import Button from '@atlaskit/button/custom-theme-button';
import Form from '@atlaskit/form';
import ModalDialog, {
  ModalBody,
  ModalHeader,
  ModalTitle,
} from '@atlaskit/modal-dialog';
import { StatsigFeatureKeys } from '@atlassian/bitbucket-features';

import { UsePipelineDefinitions } from 'src/components/pipelines/hooks/use-pipeline-definitions';
import { usePipelineVariables } from 'src/components/pipelines/hooks/use-pipeline-variables';
import { Repository } from 'src/components/pipelines/models';
import { requestBuildConfiguration } from 'src/components/pipelines/redux/actions/pipelines';
import { getYmlPath } from 'src/components/pipelines/redux/selectors/pipelines';
import { useStatsigGate } from 'src/contexts/feature-provider';

import { YML_FILE_NAME } from '../const';
import {
  BranchOption,
  FetchBranches,
  FetchCreatePipeline,
  PipelineDefinitionOption,
} from '../types';

import PipelineVariables from './PipelineVariables';
import RunPipelineBranchSelector from './RunPipelineBranchSelector';
import RunPipelineMessage from './RunPipelineMessage';
import RunPipelineSelector from './RunPipelineSelector';
import {
  FooterButtons,
  MessageWrapper,
  NoBrancheSelectorMessage,
} from './styled';

type Props = {
  isRepoReadOnly: boolean;
  isOverAllowance: boolean;
  isPipelinesDisabled: boolean;
  refName?: string;
  revision?: string;
  fetchBranches?: FetchBranches;
  fetchCreatePipeline: FetchCreatePipeline;
  openPipelinePage: (buildNumber: number) => void;
  onCloseDialog: () => void;
  settingsPageUrl?: string;
  plansPageUrl?: string;
  showBlanket?: boolean;
  usePipelineDefinitionsHook?: (
    repository: Repository,
    target: string
  ) => UsePipelineDefinitions;
};

const RunPipeline = ({
  onCloseDialog,
  isPipelinesDisabled,
  isRepoReadOnly,
  isOverAllowance,
  refName,
  revision,
  fetchBranches,
  fetchCreatePipeline,
  openPipelinePage,
  settingsPageUrl,
  plansPageUrl,
  showBlanket,
  usePipelineDefinitionsHook,
}: Props) => {
  const dispatch = useDispatch();

  const ymlPath = useSelector(getYmlPath);

  const isBitbucketPipelinesYmlSupportEnabled = useStatsigGate(
    StatsigFeatureKeys.isBitbucketPipelinesYmlSupportEnabled
  ) as boolean;
  const { createAnalyticsEvent } = useAnalyticsEvents();
  const [isCreatingPipeline, setIsCreatingPipeline] = useState<boolean>(false);
  const [createPipelineError, setCreatePipelineError] = useState<any>(null);

  const [branchOption, setBranchOption] = useState<BranchOption>(
    (refName && revision
      ? { branch: { name: refName, revision }, label: refName, value: refName }
      : undefined) as BranchOption
  );

  const [pipelineDefinitionOption, setPipelineDefinitionOption] =
    useState<PipelineDefinitionOption>();

  const { variables, selector, setVariablesValues, filteredVariables } =
    usePipelineVariables(pipelineDefinitionOption);

  const hasError = useMemo(
    () => createPipelineError || isPipelinesDisabled || isRepoReadOnly,
    [createPipelineError, isPipelinesDisabled, isRepoReadOnly]
  );

  const hasWarning = useMemo(
    () =>
      pipelineDefinitionOption &&
      pipelineDefinitionOption.pipelineDefinition.error,
    [pipelineDefinitionOption]
  );

  const isDisabled = useMemo(
    () =>
      hasError || hasWarning || isCreatingPipeline || !pipelineDefinitionOption,
    [hasError, hasWarning, isCreatingPipeline, pipelineDefinitionOption]
  );

  const target = useMemo(
    () => (branchOption ? branchOption.branch.revision : revision) || '',
    [branchOption, revision]
  );

  useEffect(() => {
    if (target) {
      dispatch(
        requestBuildConfiguration(target, isBitbucketPipelinesYmlSupportEnabled)
      );
    }
  }, [target, dispatch, isBitbucketPipelinesYmlSupportEnabled]);

  const sendAanlyticsEvent = useCallback(
    (uuid: string) => {
      createAnalyticsEvent({
        action: 'clicked',
        actionSubject: 'button',
        actionSubjectId: 'runPipelineButton',
        attributes: {
          pipelineUuid: uuid,
          isOverAllowance,
        },
      }).fire();
    },
    [createAnalyticsEvent, isOverAllowance]
  );

  const onRunPipeline = useCallback(async () => {
    setCreatePipelineError(null);
    setIsCreatingPipeline(true);

    try {
      const type =
        branchOption && branchOption.branch.name
          ? 'pipeline_ref_target'
          : 'pipeline_commit_target';

      const { build_number, uuid } = await fetchCreatePipeline({
        target: {
          type,
          selector,
          // branch or commit
          ...(type === 'pipeline_ref_target'
            ? { ref_name: branchOption.branch.name, ref_type: 'branch' }
            : { commit: { hash: revision, type: 'commit' } }),
        },
        ...filteredVariables,
      });
      sendAanlyticsEvent(uuid);
      if (!build_number)
        throw new Error('Create pipeline did not return a build_number');
      openPipelinePage(build_number);
    } catch (error) {
      setCreatePipelineError(error);
      setIsCreatingPipeline(false);
    }
  }, [
    branchOption,
    fetchCreatePipeline,
    selector,
    revision,
    sendAanlyticsEvent,
    openPipelinePage,
    filteredVariables,
  ]);

  return (
    <ModalDialog
      onClose={onCloseDialog}
      width="medium"
      shouldScrollInViewport
      isBlanketHidden={!showBlanket}
    >
      <ModalHeader>
        <ModalTitle>
          {`Run Pipeline${
            refName
              ? ` for ${refName}`
              : revision
              ? ` for commit ${revision.substring(0, 7)}`
              : ''
          }`}
        </ModalTitle>
      </ModalHeader>

      <ModalBody>
        <Form onSubmit={onRunPipeline}>
          {({ formProps }) => (
            <form {...formProps}>
              {hasError || hasWarning || isOverAllowance ? (
                <MessageWrapper>
                  <RunPipelineMessage
                    isPipelinesDisabled={isPipelinesDisabled}
                    isRepoReadOnly={isRepoReadOnly}
                    isOverAllowance={isOverAllowance}
                    isMissingPipelineDefinition={!pipelineDefinitionOption}
                    error={
                      pipelineDefinitionOption
                        ? pipelineDefinitionOption.pipelineDefinition.error
                        : undefined
                    }
                    plansPageUrl={plansPageUrl || ''}
                    settingsPageUrl={settingsPageUrl || ''}
                  />
                </MessageWrapper>
              ) : null}
              {!hasError ? (
                <>
                  {fetchBranches ? (
                    <RunPipelineBranchSelector
                      branchOption={branchOption}
                      fetchBranches={fetchBranches}
                      onChange={setBranchOption}
                    />
                  ) : (
                    <NoBrancheSelectorMessage>
                      Select the pipeline from your {YML_FILE_NAME} file that
                      you wish to run:
                    </NoBrancheSelectorMessage>
                  )}
                  <RunPipelineSelector
                    usePipelineDefinitionsHook={usePipelineDefinitionsHook}
                    pipelineDefinitionOption={pipelineDefinitionOption}
                    configurationUrl={ymlPath}
                    target={target}
                    onChange={setPipelineDefinitionOption}
                  />
                  {variables.length ? (
                    <PipelineVariables
                      variables={variables}
                      onChange={setVariablesValues}
                    />
                  ) : null}
                </>
              ) : null}
              <FooterButtons>
                <ButtonGroup>
                  <Button
                    testId="cancel-run"
                    appearance="subtle"
                    onClick={onCloseDialog}
                  >
                    Cancel
                  </Button>
                  <Button
                    testId="run-pipeline"
                    isDisabled={isDisabled}
                    appearance="primary"
                    isLoading={isCreatingPipeline}
                    type="submit"
                  >
                    Run
                  </Button>
                </ButtonGroup>
              </FooterButtons>
            </form>
          )}
        </Form>
      </ModalBody>
    </ModalDialog>
  );
};

export default memo(RunPipeline);
